<template>
  <div class="pb-3">
    <div class="d-flex i-mb-14 justify-content-between align-items-center">
      <div>
        <h5 class="i-text-heading-5 i-text-neutral-120 i-mb-2">
          What is a blocklist?
        </h5>
        <p>
          Fraudulent customers often use the same names and addresses across
          applications. To avoid missing a reapplication of a fraudulent
          customer you can add their details to a blocklist. If a document
          contains details that match details on the blocklist you will be
          alerted.
        </p>
      </div>
    </div>
    <div>
      <p>
        To upload a database of entries, create a CSV or excel file with 2
        columns: name, address and upload below. For example:
      </p>
      <div class="ui-table example-table">
        <table style="width: 80%; margin: 0 auto" aria-label="example table">
          <tr>
            <th>Name</th>
            <th>Address</th>
          </tr>
          <tr>
            <td>John Smith</td>
            <td>123 Anystreet, San Francisco, CA, USA</td>
          </tr>
        </table>
      </div>
      <p>
        Alternatively, you can type the individual entries into the text boxes
        below and press add.
      </p>
    </div>

    <div class="add-blocklist i-mt-14">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h5 class="i-text-heading-5 i-text-neutral-120">Add blocklist</h5>
        </div>
      </div>
      <div class="row mt-1">
        <div
          class="col-12 col-md-5 order-0 order-md-2 d-flex justify-content-center flex-column"
        >
          <p class="text-center mb-2">Upload new database</p>
          <label class="btn btn-md btn-default btn-block text-center">
            Browse Computer
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              style="display: none"
              @change="uploadFile"
            />
          </label>
        </div>
        <div
          class="col-12 col-md-2 order-1 d-flex justify-content-center flex-column"
        >
          <or-divider />
        </div>
        <div
          class="col-12 col-md-5 order-2 order-md-0 align-items-center flex-column"
        >
          <text-input
            id="name"
            :value="newEntry.name"
            type="text"
            placeholder="Name"
            label="Name"
            @input="newEntry.name = $event"
          />
          <text-input
            id="address"
            :value="newEntry.address"
            type="text"
            placeholder="Address"
            label="Address"
            @input="newEntry.address = $event"
          />
          <div class="d-flex justify-content-end">
            <button type="submit" class="btn btn-default" @click="add()">
              Add
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="w-100 mb-5 i-mt-14">
      <div class="ui-table card">
        <table class="table" aria-label="blocklist-table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Address</th>
              <th scope="col">Normalized Address</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(entry, index) in blocklist" :key="index">
              <td>
                <span v-if="!entry.inEdit">
                  {{ entry.name }}
                </span>
                <input
                  v-else
                  v-model="entry.name"
                  class="form-control fs-exclude"
                />
              </td>
              <td>
                <span v-if="!entry.inEdit">
                  {{ entry.address.raw }}
                </span>
                <input
                  v-else
                  v-model="entry.address.raw"
                  class="form-control fs-exclude"
                />
              </td>
              <td>
                <span>
                  {{ entry.address.normalized }}
                </span>
              </td>
              <td>
                <div v-if="!entry.inEdit" class="btn-group" role="group">
                  <button
                    type="button"
                    class="btn btn-sm btn-remove m-0"
                    @click="deleteEntry(index, entry)"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-default"
                    @click="startEdit(index, entry)"
                  >
                    Edit
                  </button>
                </div>
                <div v-else class="btn-group" role="group">
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary m-0"
                    @click="cancelEdit(index, entry)"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-default"
                    @click="updateEntry(index, entry)"
                  >
                    Save
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <i-toast></i-toast>
  </div>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import uuid from 'uuid/v4';
import { mapState } from 'vuex';
import { OrDivider, TextInput } from '@inscribe/ui-kit';
import { $event } from '../../bus';
import { iToast } from '@inscribe/signal';

export default {
  components: {
    iToast,
    'text-input': TextInput,
    'or-divider': OrDivider,
  },
  data() {
    return {
      dragging: false,
      toBeUploaded: [],
      newEntry: {
        name: null,
        address: null,
      },
    };
  },
  computed: {
    ...mapState(['blocklist']),
  },
  mounted() {
    this.$store.dispatch('getBlocklist');
  },
  methods: {
    uploadFile(event) {
      const self = this;

      for (const file of event.target.files) {
        self.toBeUploaded.push(file);
      }

      if (self.toBeUploaded.length > 0) {
        self.submitFiles();
      }
    },
    submitFiles() {
      const self = this;

      const data = new FormData();

      $event.$emit('preloader.on');

      for (const file of self.toBeUploaded) {
        const fileUUID = uuid();

        data.append(file.name, file);
        data.append(`${file.name}_uuid`, fileUUID);
      }

      self.toBeUploaded = [];

      this.$store
        .dispatch('addBlocklistings', data)
        .then((response) => {
          $event.$emit('preloader.off');
          for (const entry of response.data.data) {
            this.$store.commit('addBlocklist', entry);
          }
          this.notifySuccess({ text: 'Successfully added blocklist.' });
        })
        .catch((error) => {
          this.notifyError({
            text: `Could not add blocklist, error: ${
              error.data && error.data.message
            }.`,
          });
        });
    },
    add() {
      const self = this;
      $event.$emit('preloader.on');
      this.$store.dispatch('addBlocklist', self.newEntry).then((response) => {
        $event.$emit('preloader.off');
        this.$store.commit('addBlocklist', response.data);
        this.notifySuccess({ text: response.data.message });
      });
    },
    startEdit(index, entry) {
      entry.inEdit = true;
      Vue.set(this.blocklist, index, entry);
    },
    cancelEdit(index, entry) {
      entry.inEdit = false;
      Vue.set(this.blocklist, index, entry);
    },
    updateEntry(index, entry) {
      const updatedEntry = { name: entry.name, address: entry.address.raw };
      $event.$emit('preloader.on');
      axios
        .put(`/api/v2/blacklists/${entry.id}/`, updatedEntry)
        .then((response) => {
          $event.$emit('preloader.off');
          this.$store.commit('updateGeneric', {
            objectsName: 'blocklist',
            updatedObject: response.data,
          });
          this.notifySuccess({ text: response.data.message });
        })
        .catch((error) => {
          $event.$emit('preloader.off');
          if (error.response !== undefined && error.response.status === 403) {
            this.notifyError({
              title: 'Permission Denied',
              text: 'You do not have permission to delete this blocklist. Please contact your admin if you require this permission.',
            });
          } else {
            this.notifyError({
              text: error.data.message,
            });
          }
        });
    },
    deleteEntry(index, entry) {
      $event.$emit('preloader.on');
      axios
        .delete(`/api/v2/blacklists/${entry.id}/`)
        .then((response) => {
          $event.$emit('preloader.off');
          this.$store.commit('deleteGeneric', {
            objectsName: 'blocklist',
            idToDelete: entry.id,
          });
          this.notifySuccess({ text: response.data.message });
        })
        .catch((error) => {
          $event.$emit('preloader.off');
          if (error.response !== undefined && error.response.status === 403) {
            this.notifyError({
              title: 'Permission Denied',
              text: 'You do not have permission to delete this blocklist. Please contact your admin if you require this permission.',
            });
          } else {
            this.notifyError({
              text: error.data.message,
            });
          }
        });
    },
    notifySuccess(options) {
      this.$notify({
        group: 'api',
        type: 'success',
        title: 'Success!',
        duration: 5000,
        text: options.text,
      });
    },
    notifyError(options) {
      this.$notify({
        group: 'api',
        type: 'error',
        title: options.title || 'Error',
        duration: 5000,
        text: options.text,
      });
    },
  },
};
</script>

<style lang="scss">
.example-table {
  background: $color-navy-blue-10;
  border-radius: $border-radius-default;
  padding: 16px;
  margin: 16px 0;
}

.blocklist-page {
  margin-top: -64px;
}
</style>
